import React from "react";
import styles from "./index.module.scss";
import ImagePreview from "../../common/components/ImagePreview";
import { highlightMahatriaAndInfinitheism } from "../../common/components/HighlightMahatriaInfinitheism";
import { stringToNumber } from "../../utils/registrationUtils";
import { formatIndianCurrency } from "../../utils/commonFunctions";
interface Field {
  label: string;
  value: any; // allow object/array too
  type: string;
  helperText?: string;
}

interface DetailsBoxProps {
  fields: Field[];
  cardTitle: string;
  fieldsPerRow?: number;
}

const isPrimitive = (v: any) =>
  typeof v === "string" || typeof v === "number" || typeof v === "boolean";

const tryParseJSON = (v: any) => {
  if (typeof v !== "string") return v;
  const s = v.trim();
  if (!(s.startsWith("{") || s.startsWith("["))) return v;
  try {
    return JSON.parse(s);
  } catch {
    try {
      return JSON.parse(decodeURIComponent(s));
    } catch {
      return v;
    }
  }
};

const getMediaSrc = (value: any): string | undefined => {
  if (typeof value === "string") return value;
  if (value && typeof value === "object") {
    // common url-ish fields
    return (
      value.url ||
      value.src ||
      value.href ||
      value.link ||
      value.path ||
      value.fileUrl ||
      value.mediaUrl
    );
  }
  return undefined;
};

const RenderObjectLines: React.FC<{ obj: Record<string, any> }> = ({ obj }) => {
  return (
    <div className={styles.value}>
      {Object.entries(obj).map(([k, v], idx) => (
        <div key={idx}>
          <span>{idx + 1}</span>.{" "}
          {isPrimitive(v) ? String(v) : Array.isArray(v) ? (
            <RenderArrayLines arr={v} />
          ) : v && typeof v === "object" ? (
            <RenderObjectLines obj={v} />
          ) : (
            ""
          )}
        </div>
      ))}
    </div>
  );
};

const RenderArrayLines: React.FC<{ arr: any[] }> = ({ arr }) => {
  return (
    <div className={styles.value}>
      {arr.map((item, idx) => (
        <div key={idx}>
          {isPrimitive(item) ? (
            String(item)
          ) : Array.isArray(item) ? (
            <RenderArrayLines arr={item} />
          ) : item && typeof item === "object" ? (
            <RenderObjectLines obj={item} />
          ) : (
            ""
          )}
        </div>
      ))}
    </div>
  );
};

const RenderComplexValue: React.FC<{ value: any }> = ({ value }) => {
  const v = tryParseJSON(value);
  if (v == null) return <div className={styles.value}>-</div>;
  if (isPrimitive(v)) {
    return (
      <div className={styles.value}>
        {highlightMahatriaAndInfinitheism(String(v))}
      </div>
    );
  }
  if (Array.isArray(v)) return <RenderArrayLines arr={v} />;
  if (typeof v === "object") return <RenderObjectLines obj={v} />;
  return <div className={styles.value}>{String(v)}</div>;
};

const FieldItem: React.FC<Field> = ({ label, value, type, helperText }) => {
  const [open, setOpen] = React.useState(false);
  const [previewImageUrl, setPreviewImageUrl] = React.useState({
    image: "",
    altText: "",
  });
  if (type === "divider") {
    return (
      <div className={styles.dividerContainer}>
        <hr className={styles.divider} />
      </div>
    );
  }

  // Handle draganddrop type here
  let displayValue: any = value;
  if (type === "draganddrop") {
    try {
      let pref: any = value;
      if (typeof value === "string") {
        pref = JSON.parse(decodeURIComponent(value));
      }
      const sortedPref = Array.isArray(pref)
        ? [...pref].sort((a, b) => (a?.priorityOrder ?? 0) - (b?.priorityOrder ?? 0))
        : pref;
      displayValue = Array.isArray(sortedPref)
        ? sortedPref
            .map((session: any) =>
              session?.sessionName ? `${session.sessionName}` : `${session?.sessionId ?? ""}`,
            )
            .filter(Boolean)
            .join(", ")
        : sortedPref;
      if (Array.isArray(sortedPref) && sortedPref.length === 0) {
        displayValue = "Any HDB/MSD";
      }
      // eslint-disable-next-line no-console
      console.log("Sorted drag and drop value:", sortedPref);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("Invalid drag and drop value", e);
      displayValue = value;
    }
  }

  const _renderValue = () => {
    switch (type) {
      case "file": {
        const src = getMediaSrc(value);
        if (!src) return <div className={styles.value}>-</div>;
        return (
          <div className={styles.labelWithImage}>
            <span className={styles.label}>{label}</span>
            <div className={styles.imageContainer}>
              <img
                src={src}
                alt={label}
                className={styles.previewImageSide}
                onClick={() => {
                  setPreviewImageUrl({
                    image: src,
                    altText: label,
                  });
                  setOpen(true);
                }}
              />
              <div
                className={styles.hoverEyePreview}
                onClick={() => {
                  setPreviewImageUrl({
                    image: src,
                    altText: label,
                  });
                  setOpen(true);
                }}
              ></div>
              <ImagePreview
                imageUrl={previewImageUrl.image}
                altText={previewImageUrl.altText}
                setOpen={setOpen}
                isOpen={open}
              ></ImagePreview>
            </div>
          </div>
        );
      }
      case "video": {
        const src = getMediaSrc(value);
        if (!src) return <div className={styles.value}>-</div>;
        return (
          <div className={`${styles.labelWithImage} ${styles.videoContainer}`}>
            <video
              src={src}
              controls
              className={styles.previewVideo}
              onClick={() => {
                setPreviewImageUrl({
                  image: src,
                  altText: label,
                });
                setOpen(true);
              }}
            />
          </div>
        );
      }
      case "date":
      case "dateandtime": {
        let formattedDate = typeof value === "string" ? value : String(value);
        // Handle YYYY-MM-DD
        if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
          const [yyyy, mm, dd] = value.split("-");
          const dateObj = new Date(`${yyyy}-${mm}-${dd}`);
          formattedDate = dateObj.toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
          });
        }
        // Handle ISO 8601 format (e.g., 2025-12-27T17:01:00.000Z)
        else if (typeof value === "string") {
          const dateObj = new Date(value);
          if (!isNaN(dateObj.getTime()) && value.includes("T") && value.includes("Z")) {
            const month = dateObj.toLocaleDateString("en-GB", { month: "short" });
            const year = dateObj.getFullYear();
            const day = dateObj.getDate();
            let hours = dateObj.getHours();
            const minutes = dateObj.getMinutes().toString().padStart(2, "0");
            const isAM = hours < 12;
            let displayHour = hours % 12;
            if (displayHour === 0) displayHour = 12;
            const ampm = isAM ? "a.m" : "p.m";
            formattedDate = `${day} ${month} ${year} ${displayHour}:${minutes} ${ampm}`;
          }
        }
        return <div className={styles.value}>{formattedDate}</div>;
      }
      case "string":
      case "draganddrop":
      default: {
        // When value can be object/array, render them line-by-line
        if(type !== "draganddrop") {
          if(label?.includes("Amount") )
          {
             value = formatIndianCurrency(Math.round(stringToNumber(value)))
          }
        }
        const effective = type === "draganddrop" ? displayValue :
         value;
        return <RenderComplexValue value={effective} />;
      }
    }
  };

  return (
    <div className={styles.fieldItem}>
      {type === "file" ? (
        _renderValue()
      ) : (
        <>
          <div className={styles.label}>{highlightMahatriaAndInfinitheism(label)}</div>
          {_renderValue()}
          {helperText && <div className={styles.helperText}>{helperText}</div>}
        </>
      )}
    </div>
  );
};

export const DetailsBox: React.FC<DetailsBoxProps> = ({
  fields,
  cardTitle,
  fieldsPerRow = 3,
}) => {
  // Group fields into rows based on fieldsPerRow
  const _rows = [];
  for (let _i = 0; _i < fields.length; _i += fieldsPerRow) {
    _rows.push(fields.slice(_i, _i + fieldsPerRow));
  }

  return (
    <div className={styles.detailsBox}>
      <div className={styles.cardTitle}>{cardTitle}</div>
      {_rows.map((_row, _idx) => (
        <div className={styles.fieldsRow} key={_idx}>
          {_row.map((_field, _fidx) => (
            <FieldItem
              key={_fidx}
              label={_field.label}
              value={_field.value}
              type={_field.type}
              helperText={_field.helperText}
            />
          ))}
        </div>
      ))}
    </div>
  );
};

export default FieldItem;